import { Button, Checkbox, FormControlLabel, Icon, Menu, MenuItem, Typography } from '@material-ui/core'
import * as React from 'react'
import { connect } from 'react-redux'
import { Action, Dispatch } from 'redux'
import Bible from 'src/bible/Bible'
import BibleVerse from 'src/bible/BibleVerse'
import Immutable from 'src/util/Immutable'
import './Header2.css'
import AppState from './redux/AppState'
import * as SettingsState from './redux/SettingsState'
import SearchQuery from './search2/SearchQuery'
import SourceSearch from './search2/SourceSearch'
import { Transport } from './Transport'
import VerseHistory from './verses/VerseHistory'
import VerseSearch from './verses/VerseSearch'

interface OwnProps {
    focusedVerse: BibleVerse;
    bible: Bible;
    showVerse: boolean;
    verseHistory: Immutable<VerseHistory>;
    onVerseSearch: (query: SearchQuery) => void;
    onBookChange: (bookIndex: number) => void;
    onChapterChange: (chapterIndex: number) => void;
    onVerseChange: (verseIndex: number) => void;
}

interface StateProps {
    showInterlinearNumbers: boolean;
}

const mapStateToProps = (state: AppState): StateProps => ({
    showInterlinearNumbers: state.settings.showInterlinearNumbers
});

interface DispatchProps {
    setShowInterlinearNumbers: typeof SettingsState.setShowInterlinearNumbers;
}

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => ({
    setShowInterlinearNumbers: (showInterlinearNumbers: boolean) => dispatch(SettingsState.setShowInterlinearNumbers(showInterlinearNumbers))
});

type Props = OwnProps & StateProps & DispatchProps;

interface State {
    historyMenuAnchor: HTMLElement | null;
}

class Header2 extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);
        this.state = {
            historyMenuAnchor: null
        };
        this.previousHistory = this.previousHistory.bind(this);
        this.nextHistory = this.nextHistory.bind(this);
        this.openHistoryMenu = this.openHistoryMenu.bind(this);
        this.closeHistoryMenu = this.closeHistoryMenu.bind(this);
        this.onHistoryClick = this.onHistoryClick.bind(this);
        this.onVerseSelect = this.onVerseSelect.bind(this);
    }

    // tslint:disable:jsx-no-lambda
    public render(): React.ReactNode {
        const disabledButtons = {
            previousHistory: this.props.verseHistory.get().atStart(),
            nextHistory: this.props.verseHistory.get().atEnd()
        };

        return (
            <header className="Header2">
                <div className="Header2_Left">
                    <SourceSearch onSearch={this.props.onVerseSearch} />

                    <VerseSearch
                        bible={this.props.bible}
                        onSelect={this.onVerseSelect} />

                    <div className="Header2_Transport">
                        <Transport
                            focusedVerse={this.props.focusedVerse}
                            bible={this.props.bible}
                            showVerse={this.props.showVerse}
                            onBookChange={this.props.onBookChange}
                            onChapterChange={this.props.onChapterChange}
                            onVerseChange={this.props.onVerseChange} />
                    </div>

                    <div className="Header2_History">
                        <Button
                            className="Header2_HistoryButton"
                            disabled={disabledButtons.previousHistory}
                            onClick={this.previousHistory}>
                            <Icon color={disabledButtons.previousHistory ? 'disabled': undefined}>chevron_left</Icon>
                        </Button>
                        <Button
                            className="Header2_HistoryTextButton"
                            onClick={this.openHistoryMenu}>
                            <Typography variant="h6">History</Typography>
                        </Button>
                        <Button
                            className="Header2_HistoryButton"
                            disabled={disabledButtons.nextHistory}
                            onClick={this.nextHistory}>
                            <Icon color={disabledButtons.nextHistory ? 'disabled': undefined}>chevron_right</Icon>
                        </Button>
                    </div>
                </div>

                <div className="Header2_Right">
                    <FormControlLabel
                        className="Header2_ShowWordNumbers"
                        control={<Checkbox
                            checked={this.props.showInterlinearNumbers}
                            onChange={(event, checked) => this.props.setShowInterlinearNumbers(checked)}
                            color="secondary" />}
                        label="Show word position numbers"
                        labelPlacement="start" />
                </div>

                {this.renderHistoryMenu()}
            </header>
        );
    }

    private renderHistoryMenu(): React.ReactNode {
        const verseHistory = this.props.verseHistory.get();
        // tslint:disable:jsx-no-lambda
        return (
            <Menu anchorEl={this.state.historyMenuAnchor} open={this.state.historyMenuAnchor !== null} onClose={this.closeHistoryMenu}>
                {verseHistory.getAll().map((verse, index) =>
                    <MenuItem
                        key={verse.getIndex()}
                        className="Header2_MenuItem"
                        selected={verseHistory.isCurrentPosition(index)}
                        onClick={() => this.onHistoryClick(index)}>
                        {verse.getName()}
                    </MenuItem>
                )}
            </Menu>
        );
        // tslint:enable:jsx-no-lambda
    }

    private previousHistory(): void {
        const verseHistory = this.props.verseHistory.get();
        verseHistory.goBack();
        this.props.onVerseChange(verseHistory.getCurrent().getIndex());
    }

    private nextHistory(): void {
        const verseHistory = this.props.verseHistory.get();
        verseHistory.goForward();
        this.props.onVerseChange(verseHistory.getCurrent().getIndex());
    }

    private openHistoryMenu(event: React.MouseEvent<HTMLElement>): void {
        this.setState({ historyMenuAnchor: event.currentTarget });
    }

    private closeHistoryMenu(): void {
        this.setState({ historyMenuAnchor: null });
    }

    private onHistoryClick(historyIndex: number): void {
        const verseHistory = this.props.verseHistory.get();
        verseHistory.goTo(historyIndex);
        this.props.onVerseChange(verseHistory.getCurrent().getIndex());
        this.closeHistoryMenu();
    }

    private onVerseSelect(verse: BibleVerse): void {
        this.props.onVerseChange(verse.getIndex());
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Header2);
